import { simhashSimilarity } from "./fingerprints";
import type {
  FamilySignatureMatch,
  FingerprintMatch,
  HashAlgorithm,
  KnownHashMatch,
} from "./types";

export interface ThreatContext {
  source: string;
  values: string[];
}

// A structural reference point: the simhash of a known sample plus the
// similarity at which its author is willing to stand behind the attribution.
export interface KnownFingerprintRecord {
  family: string;
  variant: string;
  simhash64: string;
  threshold: number;
  source: string;
  reference: string;
  firstSeen?: string;
  trust?: "built-in" | "verified-pack" | "local-unverified";
  packId?: string;
}

export interface KnownHashRecord {
  id: string;
  family: string;
  variant: string;
  hashes: Partial<Record<HashAlgorithm, string>>;
  source: string;
  reference: string;
  firstSeen?: string;
  trust?: "built-in" | "verified-pack" | "local-unverified";
  packId?: string;
}

interface FamilySignal {
  id: string;
  label: string;
  group: string;
  weight: number;
  patterns: RegExp[];
}

interface FamilySignature {
  id: string;
  family: string;
  variant: string;
  reference: string;
  minScore: number;
  minSignals: number;
  highConfidenceScore: number;
  minGroupHits?: Record<string, number>;
  excludes?: RegExp[];
  signals: FamilySignal[];
}

const MALWARE_BAZAAR = "MalwareBazaar";
const SILENTNET_RESEARCH =
  "https://github.com/digitalforensiker/silentnet-analysis";
const WEEDHACK_RESEARCH = "https://0xresetti.github.io/weedhack.html";
const MCAFEE_WEEDHACK_RESEARCH =
  "https://www.mcafee.com/blogs/other-blogs/mcafee-labs/weedhack-minecraft-malware-as-a-service-campaign-research/";
const POLYSWARM_WEEDHACK_INTEL =
  "https://blog.polyswarm.io/from-minecraft-mods-to-malware-as-a-service-inside-the-weedhack-ecosystem";
const KRYPTON_HYBRID_ANALYSIS =
  "https://hybrid-analysis.com/sample/b2173196d06aa3458bb0c80b6c3ca5b73c1f9f2ee825f0b5f853d46a59e486eb/6a5a831633340cea0a0f1528";
const KRYPTON_ANYRUN =
  "https://any.run/report/5813191fa31d7c0068e135f2e1c4a541e95cdb68e8fa5df43d5ca63514b1ed98/1ae08724-3b62-4af0-82f9-8d9b19dc32b1";
const FRACTUREISER_RESEARCH =
  "https://github.com/trigram-mrp/fractureiser/blob/main/docs/tech.md";
const BITDEFENDER_FRACTUREISER_RESEARCH =
  "https://www.bitdefender.com/en-us/blog/labs/infected-minecraft-mods-lead-to-multi-stage-multi-platform-infostealer-malware";
const BAIKAL_RESEARCH =
  "https://research.checkpoint.com/2025/minecraft-mod-malware-stargazers/";
const SKYRAGE_RESEARCH =
  "https://bazaar.abuse.ch/sample/ff16aa0148d870eead1b3099e6a6ddf865aadd0f482e2c8020ab4152d5ff6652/";

/*
 * This is deliberately an offline, auditable seed set. A SHA-256 match is
 * deterministic and does not send the user's file or hash to a third party.
 * Behavior signatures below cover repacks and newly generated MaaS payloads.
 */
const KNOWN_HASHES: KnownHashRecord[] = [
  {
    id: "weedhack-krypton-v15-b2173196",
    family: "WeedHack",
    variant: "Krypton v15 loader and session stealer",
    hashes: {
      sha256:
        "b2173196d06aa3458bb0c80b6c3ca5b73c1f9f2ee825f0b5f853d46a59e486eb",
      sha1: "0827b2399446ad2ee75ffdf9ed2b7373f825e9ed",
      md5: "f504d57ba65f46465c28ba3ccbb101b1",
    },
    source: "JarLab verified sample and Hybrid Analysis",
    reference: KRYPTON_HYBRID_ANALYSIS,
    firstSeen: "2026-07-17",
  },
  {
    id: "weedhack-krypton-5813191f",
    family: "WeedHack",
    variant: "Krypton client WeedHack loader",
    hashes: {
      sha256:
        "5813191fa31d7c0068e135f2e1c4a541e95cdb68e8fa5df43d5ca63514b1ed98",
      sha1: "10128f982a1efa7f14fb5c37ce119a2e00401bc0",
      md5: "d0cf7f5844529c59980f9118755c805f",
    },
    source: "ANY.RUN public sandbox",
    reference: KRYPTON_ANYRUN,
    firstSeen: "2026-05-23",
  },
  {
    id: "weedhack-donutdupe-2e1e891d",
    family: "WeedHack",
    variant: "DonutDupe 2.1.1 stealer",
    hashes: {
      sha256:
        "2e1e891da17c20d5d8eacc82374b84cfb30536c9b642cfb26e8ba6d8d3bf0a90",
      sha1: "5e3eefb3de963181f5f109b59e087c4596f03ac3",
      md5: "667e9ceee1df535c94ebbd5ca9a94cca",
    },
    source: "ANY.RUN public sandbox",
    reference:
      "https://any.run/report/2e1e891da17c20d5d8eacc82374b84cfb30536c9b642cfb26e8ba6d8d3bf0a90/e1413796-91b5-4755-a19d-fa45619b4d4c",
    firstSeen: "2026-03-19",
  },
  ...(
    [
      [
        "cf0696839759101809fda010f67b8d7bdeeb28be3f9cdbc10044bfb626cd9f81",
        "Krypton session-token exfiltration class",
      ],
      [
        "0d7767cef8e86c79d91e5f44ffd2f9e0968fbb0396235167238abd174df5fca2",
        "Krypton WeedHack module loader class",
      ],
      [
        "1c4db7894f720735756317c451b174cbb58abe745972881c4b290949483fcbd8",
        "Krypton Fabric credential bootstrap class",
      ],
      [
        "7a50e885bb12a7523087d0a41f413ea1a36d6e3dcbe52459742c701e6356a903",
        "Krypton standalone javaw bootstrap class",
      ],
      [
        "95d1b31ef679f669b7f2bb45521bf5f73ee1b2993d09c71d1a971771ac4defcd",
        "Krypton blockchain resolver class",
      ],
      [
        "59d732339557603fa571cf8ed5c0562485ccfeb2ce9b43d8bd9baa4e7b600ecc",
        "Krypton DNS-over-HTTPS transport class",
      ],
      [
        "bc6edbf2d3afb9320da16ea29504c446c903893639c96c22685d6aed11c5b144",
        "Krypton rotating-XOR decoder class",
      ],
      [
        "c1993b6c62c52bb1631105d70b5be5cb513171ce2c4ae9cd998cb1ea8b88648c",
        "Krypton invokedynamic bootstrap class",
      ],
    ] as const
  ).map<KnownHashRecord>(([hash, variant], index) => ({
    id: `weedhack-krypton-component-${index + 1}-${hash.slice(0, 8)}`,
    family: "WeedHack",
    variant,
    hashes: { sha256: hash },
    source: "JarLab verified Krypton v15 sample",
    reference: KRYPTON_HYBRID_ANALYSIS,
    firstSeen: "2026-07-23",
  })),
  ...[
    "f2100e1f73477bc565f8909e069942dac1f884654ed4ba213ca9a84b1e761ab8",
    "d3f2464ae0e48218e1d48bdfab8301ee5236f7624adcdba1720dc27058461076",
    "b982fbafa954a8dcf7cfcffe31bcf75a86b052b1f01cf535ffcafd2c48a56b60",
    "29546a03e07bfeb3025313b12671c758ced1c4921a4bc859a7ab40ec52584cdb",
    "f790346bece8e448313f701586cc7fd18291dfda721aae8d86ebfacf14055645",
    "5f7680feccc15814299df3c3c11e9b1c4f33069aac5a19c03b87e15f30c2312b",
    "256b5b5d0524c442261028767b94f7188b0b81663b50c63300fca7733a04ea7d",
    "e123d1f7cbea562237f7a5f50638d148fb58048c9ad095e0b0ad52e43bfedad0",
    "d468983f98ff100ad8fd613315af4c88d67bec76782b66b260c413c587987bf0",
    "ef31bb219b84744e02f90947f31a25958b2b34524ed3795799ed6eff876e4bcd",
    "5d537a058ec19e6ceea593738f122b777d866042ea0bad194539757de13c46f4",
    "697ee941abee202d8e84e5e3fed8b9f34eea8772ee56dc867fce017507a5eeaf",
    "f9a6911e8d9130c779db2e79f901d75d90f9e3ad08c36e7fb927959b7d988bae",
    "86f8c0a92eb9aba3c3416667361652a9e11b6ddc1119bb5b3564bc107b950ddb",
    "790ff5cda1668e7aa390fbb1682a4d578195aa40542f64b7b6d56a6eccde12c9",
    "db533717da686f3b76b9de85ecd80d326a14572056a33d31f794bffbffd96c26",
    "8b53f53f72b8fef755666b6f239c06a69a9940e1b9f5d19e022150750035fa80",
    "6b2218999ac27f6085cb02f693a3c99bd6abedfc20e00e22709e526015c89f4e",
    "9682adf40a3621ffe5e1b426c5b90d0ed70e663738857bb4d18d37d93bbd4e6c",
    "3951533d56803cd5d708014b4eed7e30349b4c4ba43f7d843133b3a5e2992ce6",
    "37bcec9ba357a2cb13a4f0f910e40f01e33973a5d637a3487c298105ae1ff22b",
    "08a64523d7a05defb6cc5c87df340d76f9ef7ccc9623a0d338981be4cd9cd6c7",
    "36a89f65fe2d693a094b51495f3a84d0f4f2ae7276649952d6f78c85282e6f6d",
    "d4918dbf7ada4883d89a01dcf5332413b7773b12d0e479f2cf502e3245c93720",
    "cf9bc0a3e01a7b466bc35dbf88563adf61c884ad5fb2b28afd1298a5f723f370",
    "d28bc760f0b80905ea199809ad7ebfc73ab12aeab0ad3ee2dd11990657d2d9eb",
    "7f69a67316872186fd440b4126a77c419f14b459542181c5e12feb49a223fd39",
    "902cb8bfa3863df299ac804dc77e3e9366658b2b3c2ec5d3a1bdaf2e52520ce5",
    "2a5baf86a3e982eb557dffffabb619c9e80581d41cdc4b85b06367b588647a7d",
    "ea595940815a11901bd99214b26d9528034f7182bd6c3bf2fe3179ac92e00afc",
    "dba9908f63f5f32405f7a728f37979e743814532378cabc4f0e9f24c34197c60",
    "77dd1dd9b12699c64ab31c0140b28c70339014a0969f3bb7a79068f5b8f3f34a",
    "32e743d1e3957f35651a9d15a83bc128b82108c17b0fa64d63fa98b1d326fc9d",
    "a81ba29e550beae21fff69bfe0478249eb7078b173f9cf2040d74df299fc9d5b",
    "14118a6070f89baafd5f2aeaf2df7535a8053f99944453584f0d1efeb6501ac3",
    "b9f71ed4b08c93a7fc5468bee23660e3129e1cf9c84100d4d40ad70fb7c851fa",
    "88d8ac22ea323842cd760d645daea54043739d45a0fa61fd72fe5a5c9acb5e69",
    "fdceafe4dcf9cf6d23b2033824275c08ec73d6b01adc644416e43ecca94c89c9",
    "226889380ca1695158cd42ba4b7d89352c4fa74010583669ac89ad69fdefd566",
    "1b5ca4d2b5eb23041da0f6effdc408d50768701d4140a21c9fbd244f9458d720",
    "c7691712d794d4ef582c591566bf5fda76a364b0bcdad315adbaaec8607ad0f3",
  ].map<KnownHashRecord>((hash, index) => ({
    id: `weedhack-polyswarm-${index + 1}-${hash.slice(0, 8)}`,
    family: "WeedHack",
    variant: "Public WeedHack sample",
    hashes: { sha256: hash },
    source: "PolySwarm threat intelligence",
    reference: POLYSWARM_WEEDHACK_INTEL,
    firstSeen: "2026",
  })),
  {
    id: "weedhack-stage1-f91714",
    family: "WeedHack",
    variant: "Stage 1 loader",
    hashes: {
      sha256:
        "f91714f89616002c6c1411233470f58e74fad7cb5a7da6f77aa6082f5d2e8771",
      sha1: "f7911f5be3d08da95dcda8afb1beb8e462376f9d",
      md5: "d991a7c9e2c3b269975404405a79adbc",
    },
    source: MALWARE_BAZAAR,
    reference:
      "https://bazaar.abuse.ch/sample/f91714f89616002c6c1411233470f58e74fad7cb5a7da6f77aa6082f5d2e8771/",
    firstSeen: "2026-01-26",
  },
  {
    id: "weedhack-stage1-a3d98b",
    family: "WeedHack",
    variant: "Stage 1 v2 loader",
    hashes: {
      sha256:
        "a3d98b61bb0f409aaaf0d4f1d1029e35211ba6fbd6419082677a2d29ee7e403e",
      sha1: "beb5f99a8239f4c32770b24c758ef767f4d8701a",
      md5: "099d783c5beafa513332639db766dc73",
    },
    source: MALWARE_BAZAAR,
    reference:
      "https://bazaar.abuse.ch/sample/a3d98b61bb0f409aaaf0d4f1d1029e35211ba6fbd6419082677a2d29ee7e403e/",
    firstSeen: "2026-02-17",
  },
  {
    id: "weedhack-donut-77f79f",
    family: "WeedHack",
    variant: "Donut Duping v2 lure",
    hashes: {
      sha256:
        "77f79f4ba6edb627f1edece692f3f22700b327ab6de17859fd93daca855fe4e0",
      sha1: "c3dcd506eed973b7f2f2c869ff483ae1f4a5ce31",
      md5: "88c10a04780bf87375b4bc4e040d86b9",
    },
    source: MALWARE_BAZAAR,
    reference:
      "https://bazaar.abuse.ch/sample/77f79f4ba6edb627f1edece692f3f22700b327ab6de17859fd93daca855fe4e0/",
    firstSeen: "2026-04-19",
  },
  {
    id: "weedhack-krypton-c5215f",
    family: "WeedHack",
    variant: "Krypton / SilentNet-tagged lure",
    hashes: {
      sha256:
        "c5215f48f6038ec5d14d29e380b9760135f328bcf49c6f78571e0cf2b23278d4",
      sha1: "3d0ebd84186d1589b1c665804a5cf9e5908ed250",
      md5: "057ea0de4fc7f201d25d27747930b2da",
    },
    source: MALWARE_BAZAAR,
    reference:
      "https://bazaar.abuse.ch/sample/c5215f48f6038ec5d14d29e380b9760135f328bcf49c6f78571e0cf2b23278d4/",
    firstSeen: "2026-04-26",
  },
  ...[
    "9ab31332428640deb133e870198a8ea9a00c7613ebc6bc6388b47d1a975390c4",
    "874c9c65bb907c883e6cabe90268e47daebf87b7e3a263a4ec5e84f0847e9b03",
    "1016d276642187847307e046952c1421a8ca7623f8bec81bced52e541607ebdd",
    "e71f48cc967b458e1e0c20ced205b2516ff27914106e163d34d5c48eaafbe03e",
    "f54a99d6a61259ce31e6e585605c165b2b7788879a25781a7c6fd912bec16dcf",
    "1495969071446509364a922b070d2a3a3fcf4fd785ca893b454e821ea44b4372",
  ].map<KnownHashRecord>((hash, index) => ({
    id: `weedhack-bazaar-${index + 1}-${hash.slice(0, 8)}`,
    family: "WeedHack",
    variant: "Generated JAR payload",
    hashes: { sha256: hash },
    source: MALWARE_BAZAAR,
    reference: `https://bazaar.abuse.ch/sample/${hash}/`,
    firstSeen: "2026-04",
  })),
  {
    id: "weedhack-stage2-module",
    family: "WeedHack",
    variant: "Stage 2 stealer module",
    hashes: {
      sha256:
        "686c5194070dc5d4b971b73347f7348b4433f749b32b1c06cf47ebb692027403",
    },
    source: "Public WeedHack reverse-engineering repository",
    reference: "https://github.com/animefre4k/weedhack-malware",
  },
  {
    id: "weedhack-jnic-x64",
    family: "WeedHack",
    variant: "JNIC Windows x64 payload",
    hashes: {
      sha256:
        "489b49fa386094ecd622008c12b3851f1c47f857f15e08abb4cf18e85a059ee0",
    },
    source: "Public WeedHack reverse-engineering repository",
    reference: "https://github.com/animefre4k/weedhack-malware",
  },
  {
    id: "weedhack-jnic-arm64",
    family: "WeedHack",
    variant: "JNIC Windows ARM64 payload",
    hashes: {
      sha256:
        "0c21b4b9c5335cbb4082a88955384889b198d593439ce2ccd4ba17a342264135",
    },
    source: "Public WeedHack reverse-engineering repository",
    reference: "https://github.com/animefre4k/weedhack-malware",
  },
  ...[
    "468147b73b401b695241c10ac6777fb10194789dde88e0edd2019f22f97c2312",
    "1193341ecc079c97de7e178cf4aec9da4a4089f892cc01ef1efda4c2431abbde",
    "7b3eb76cf18d4b634cb69ac5e3952fd6d93c4c24e04f80f13936cacfb6e3d338",
    "ba338aaffbd17568d998894b70779d974f51bfa889947958bad3d3d49a434388",
    "a83d05b6e86692f1efb153cdcccf14099d0470fc612c4f70eee2bbd0357bd6e2",
    "3461c96e964054cb9f6d204db3f0f96af411f50c3dc1ee38036611f51c4904b1",
    "872ee6387647110afa6d6385b854cf00107405c091ddaf4571830289debdc6dc",
    "3e5c65edd61d8f1b95ea79451758c43cf4483bf1a262942f1ca3a5229f107b3c",
    "afd6aa4c10a70772c9bab91cb3ba5c6a6fb9220348f46334391f9f8b07bcf748",
    "3539dbf3d81846f8d7276dd66c63c210d6f50d622d1fad39ee371d1dab5760e2",
    "c9ccc4baa54d90f46e7d05ca19cb00db6bae36eb31d5a9ab6b380ae88473d494",
    "cee20b0ab5a4f45984e8692355fd73082c52120875101bd3ff87d07c82646ee7",
    "181897e48ef1effc738d8aa1dcb2f08d1427eec5d57609c68629992fc04b5dfe",
  ].map<KnownHashRecord>((hash, index) => ({
    id: `silentnet-bazaar-${index + 1}-${hash.slice(0, 8)}`,
    family: "SilentNet",
    variant: "Minecraft stealer JAR",
    hashes: { sha256: hash },
    source: MALWARE_BAZAAR,
    reference: `https://bazaar.abuse.ch/sample/${hash}/`,
    firstSeen: "2026",
  })),
  {
    id: "silentnet-donutdupe-10d1620d",
    family: "SilentNet",
    variant: "DonutDupe SilentNet stealer",
    hashes: {
      sha256:
        "10d1620dbbb762a94bea71602b7f6d3a3167bf5f4e1cd7f3ab49aeac74c2a573",
      sha1: "c08a7d452c22abb14f824d238582b607804d2546",
      md5: "b8f5b51ecf230e52c3bd7bf4a30c59f0",
    },
    source: MALWARE_BAZAAR,
    reference:
      "https://bazaar.abuse.ch/sample/10d1620dbbb762a94bea71602b7f6d3a3167bf5f4e1cd7f3ab49aeac74c2a573/",
    firstSeen: "2026-06-21",
  },
  {
    id: "silentnet-xor-repack-01f625",
    family: "SilentNet",
    variant: "XOR-packed Stage 2 downloader",
    hashes: {
      sha256:
        "01f625e4a885eb6d9253b978e35a3090fd7b026140410d8bcda6182301f1ade8",
      sha1: "99419a7111a78a5b4a6ee33402546d146f7814a2",
      md5: "a59b506e03b4266048a13183fa76d367",
    },
    source: "JarLab verified sample corpus",
    reference: SILENTNET_RESEARCH,
    firstSeen: "2026-07-23",
  },
  {
    id: "silentnet-character-repack-2eb3a572",
    family: "SilentNet",
    variant: "Unicode-packed Stage 2 downloader",
    hashes: {
      sha256:
        "2eb3a5727fe07993aa7d4e37a576b58edf385bc0bb2bfff0690fb3e87e58008d",
      sha1: "96b364d99451417107af70be083240d7c696817b",
      md5: "b4e0bdf63752dcf7c0e3fb079e507723",
    },
    source: "JarLab verified supplied sample",
    reference: SILENTNET_RESEARCH,
    firstSeen: "2026-08-24",
  },
  ...(
    [
      [
        "351095d8c421e99573178e68e60cd58ec81cc00cea29c7699166500d71370ca8",
        "XOR-packed downloader class",
      ],
      [
        "95f17eeb8406ac0f7f435893c713fed6ccf15102fbaec72e238131bbd0da647a",
        "custom HTTPS and DoH transport class",
      ],
      [
        "0f69caddc72038e1f1e41ac42e8e2f5288416056a19bc36352e2bf082d87ceca",
        "Fabric credential bootstrap class",
      ],
      [
        "de3909ed48826d7f708a85636b7107aebde8cd60d9eea65731bb59bf628d1948",
        "standalone restart bootstrap class",
      ],
      [
        "8b9c1fe25e389e91ee0b51485fa85b8081c3cc58ee41d0b120b1103016585c36",
        "TLS socket factory helper class",
      ],
      [
        "22b52ae42f121f10acc5374d86a89a776bf9a8884e531444a6c556edeaba4c8b",
        "HTTP response helper class",
      ],
      [
        "2d2dbb634f26f3078e24944e697c0752c8f655ee93f82538a4408af2f6191557",
        "permissive trust manager helper class",
      ],
    ] as const
  ).map<KnownHashRecord>(([hash, variant], index) => ({
    id: `silentnet-xor-component-${index + 1}-${hash.slice(0, 8)}`,
    family: "SilentNet",
    variant,
    hashes: { sha256: hash },
    source: "JarLab verified sample corpus",
    reference: SILENTNET_RESEARCH,
    firstSeen: "2026-07-23",
  })),
  ...(
    [
      [
        "f5fa1b4275bb2d47ecfd632fcb4160a827f9d124e328b1140ac16462ebfda654",
        "Unicode-packed downloader and Python-stage launcher class",
      ],
      [
        "5080cd7bc1102e999744fbddd78fb50e5a73a4342d010c23238f38c2c0208f84",
        "custom HTTPS and DNS-over-HTTPS transport class",
      ],
      [
        "6b7a19694f989d887104607229f70700c4b1fbc16936cf20dc39a2ee8f183701",
        "Fabric session collection bootstrap class",
      ],
      [
        "0f921135f8f0dd7319013905ec4bcb3ff9cfef1c1c47a4bb0c2ffbb45d7ebb6b",
        "standalone restart bootstrap class",
      ],
      [
        "c8c174ca25cddf978dc793e1421a91f02994c91a59f5df9891b4373b6ea5225f",
        "HTTP cache record helper class",
      ],
      [
        "6552397361146ff45cefcf3166124a45618d3a5e97685a6d5dcba3338a7825e9",
        "HTTP response helper class",
      ],
      [
        "8f15c6a0d972807d9ae6bf52c360aef5ed9772bbec094b9447bffea26da66881",
        "permissive TLS trust manager class",
      ],
    ] as const
  ).map<KnownHashRecord>(([hash, variant], index) => ({
    id: `silentnet-character-component-${index + 1}-${hash.slice(0, 8)}`,
    family: "SilentNet",
    variant,
    hashes: { sha256: hash },
    source: "JarLab verified supplied sample",
    reference: SILENTNET_RESEARCH,
    firstSeen: "2026-08-24",
  })),
  {
    id: "silentnet-cwb-d58d1b",
    family: "SilentNet",
    variant: "CWB Fabric lure",
    hashes: {
      sha256:
        "d58d1b6f1c3302406278e9977b47552e9422ab36d750e666c2a9def34fe4b901",
      sha1: "7e16e15710d33216cc45f69dde55c1190e8e54f4",
      md5: "15058ca727c500ef9633a7bc080048d9",
    },
    source: "Joe Sandbox",
    reference: "https://www.joesandbox.com/analysis/1904807/1/html",
    firstSeen: "2026-04-26",
  },
  {
    id: "silentnet-gamble-rig-afd876",
    family: "SilentNet",
    variant: "Gamble Rig client stealer",
    hashes: {
      sha256:
        "afd876bb8b0daef565ceca1aaabee349e8e6c7fa480dd901675b991092aefa7f",
      sha1: "0a9653f3a453d9e709690737ee7a15cc25cc6c29",
      md5: "befec59a6ccc8a4fc17144080ab33dbf",
    },
    source: MALWARE_BAZAAR,
    reference:
      "https://bazaar.abuse.ch/sample/afd876bb8b0daef565ceca1aaabee349e8e6c7fa480dd901675b991092aefa7f/",
    firstSeen: "2026",
  },
  ...(
    [
      [
        "618cd001c2cf432346bc6beb98236cbfcf7feb50ceb2dfd43c6f0e58872dee67",
        "a8ebca29e7d5fb3d989ba080e8ac66fe6670c5ca",
        "ffc2492ec2a41b9dfba59d8a1979b765",
        "Stage 1 loader",
      ],
      [
        "09db02307346921bb4e49dcf6f4b89c49584b994c08dbbecadd7f087c1c41961",
        "3d233c5f4c34c5865dd33e85a679081d2409fc49",
        "bd91fcebd2baf7d0b5304245dab11b4e",
        "Wurst client lure",
      ],
      [
        "c87ec10a6b3477e10c158bb5b7c3cd1cd43fd83f619527a7e7fcdd87103b5a29",
        "2b0ab5e381efc13bcfbcdca280a0eca93a110b8b",
        "8d91c609f4d669890560a14c29bd24f0",
        "Crypto-drainer payload",
      ],
      [
        "f3eb7eec922bed969e7e971af94de816f03fbaaf2093e504c4c7a925b4e728ac",
        "c42668f8f7ea619e87e60783e39bacbaf7aedfd5",
        "1c0aa2d5a91dbe6cb1f687b8a3619c89",
        "Glazed client lure",
      ],
    ] as const
  ).map<KnownHashRecord>(([sha256Hash, sha1Hash, md5Hash, variant]) => ({
    id: `weedhack-bazaar-expanded-${sha256Hash.slice(0, 8)}`,
    family: "WeedHack",
    variant,
    hashes: {
      sha256: sha256Hash,
      sha1: sha1Hash,
      md5: md5Hash,
    },
    source: MALWARE_BAZAAR,
    reference: `https://bazaar.abuse.ch/sample/${sha256Hash}/`,
    firstSeen: "2026",
  })),
  ...(
    [
      [
        "72d80252032723c3b7f9c04be3fcdf69e96dad41cd8b7968266b296643357316",
        "18d2aec50aace75a08802583b9811f3e5320727a",
        "90eac76c66312a53a00774a796c3038b",
        "Prestige Stealer",
        "Minecraft credential stealer and dropper",
      ],
      [
        "547408592909b01781caf568307f415fe324530680dee4592fa8f93e71b37377",
        "2a11d6568801442a2abb4dc6b7c1ba7b1cc3797f",
        "cc0ce6e03bec7400a038262f2b2065f5",
        "Compromised SkyHanni",
        "Compromised Stage 1 loader",
      ],
      [
        "81b2df38e98a5c26627139784e31b4f633ba33646571950d321fee80eb9f5002",
        "50e28b6cf98c098ac08ed83bf8a6de8588234daf",
        "9376a4c4cf28d27098520d0c52157a78",
        "ArkanixStealer",
        "Minecraft stealer JAR",
      ],
      [
        "fbb4c502125448d2cb622eca86918c91a480c5d194051c16b838a21c5806cd3f",
        "6be9c6d28481019882e29e584ac5255842c24f95",
        "04abb3953e5890d3fd6dbde7b4d2be70",
        "Niggaware",
        "Stage 1 loader",
      ],
      [
        "350b1b7f1556a04e33dfbde3354b02d907e67e9c00d73c79670ea5e20f0bd1bf",
        "",
        "4ee00f36e317c6a2008e5f49e996b369",
        "Niggaware",
        "Dropped Stage 2 payload",
      ],
    ] as const
  ).map<KnownHashRecord>(
    ([sha256Hash, sha1Hash, md5Hash, family, variant]) => ({
      id: `minecraft-malware-${sha256Hash.slice(0, 8)}`,
      family,
      variant,
      hashes: {
        sha256: sha256Hash,
        ...(sha1Hash ? { sha1: sha1Hash } : {}),
        md5: md5Hash,
      },
      source: MALWARE_BAZAAR,
      reference: `https://bazaar.abuse.ch/sample/${sha256Hash}/`,
      firstSeen: "2026",
    }),
  ),
  ...[
    "33677ca0e4c565b1f34baa74a79c09a3b690bf41",
    "2db855a7f40c015f8c9ca7cbab69e1f1aafa210b",
    "284a4449e58868036b2bafdfb5a210fd0480ef4a",
    "0c6576bdc6d1b92d581c18f3a150905ad97fa080",
    "c55c3e9d6a4355f36b0710ab189d5131a290df26",
    "32536577d5bb074abd493ad98dc12ccc86f30172",
    "a4b6385d1140c111549d95eab25cb51922eefba2",
  ].map<KnownHashRecord>((hash, index) => ({
    id: `fractureiser-stage0-${index + 1}-${hash.slice(0, 8)}`,
    family: "Fractureiser",
    variant: "Infected mod or plugin Stage 0",
    hashes: { sha1: hash },
    source: "Minecraft community incident response",
    reference: FRACTUREISER_RESEARCH,
    firstSeen: "2023-06",
  })),
  ...(
    [
      [
        "sha1",
        "b0752dcf01d56f420cb084c84b641b9c132e8a73",
        "Stage 0 infected mod",
      ],
      [
        "sha1",
        "282adb0edc52ce955932de48ef06df36e1050ada",
        "Stage 1 downloader",
      ],
      [
        "sha1",
        "e50eadd3293e35e60e89d1914bbc67ab597c8721",
        "Stage 2 payload",
      ],
      [
        "sha1",
        "2de8f42871213f17771be2943e5f9da3b0a94ad2",
        "Stage 3 infostealer payload",
      ],
      [
        "md5",
        "e69b50d1d58056fc770c88c514af9a82",
        "Early Stage 2 development sample",
      ],
      [
        "md5",
        "0e583c572ad823330b9e34d871fcc2df",
        "Early Stage 0 development sample",
      ],
    ] as const
  ).map<KnownHashRecord>(([algorithm, hash, variant]) => ({
    id: `fractureiser-bitdefender-${hash.slice(0, 8)}`,
    family: "Fractureiser",
    variant,
    hashes: { [algorithm]: hash },
    source: "Bitdefender Labs",
    reference: BITDEFENDER_FRACTUREISER_RESEARCH,
    firstSeen: "2023",
  })),
  {
    id: "fractureiser-floating-damage-1d1aaccd",
    family: "Fractureiser",
    variant: "Infected Floating Damage Stage 0",
    hashes: {
      sha256:
        "1d1aaccdc13244e980c0c024610ecc77ea2674a33a52129edf1bb4ce3b2cc2fc",
    },
    source: "Minecraft community incident response",
    reference: FRACTUREISER_RESEARCH,
    firstSeen: "2023-06",
  },
  ...(
    [
      ["dc43c4685c3f47808ac207d1667cc1eb915b2d82", "Stage 1 dl.jar"],
      ["52d08736543a240b0cbbbf2da03691ae525bb119", "Stage 2 payload"],
      ["6ec85c8112c25abe4a71998eb32480d266408863", "Stage 2 payload variant"],
      ["c2d0c87a1fe99e3c44a52c48d8bcf65a67b3e9a5", "Stage 3 payload"],
      ["e299bf5a025f5c3fff45d017c3c2f467fa599915", "Stage 3 payload variant"],
    ] as const
  ).map<KnownHashRecord>(([hash, variant]) => ({
    id: `fractureiser-stage-${hash.slice(0, 8)}`,
    family: "Fractureiser",
    variant,
    hashes: { sha1: hash },
    source: "Minecraft community incident response",
    reference: FRACTUREISER_RESEARCH,
    firstSeen: "2023-06",
  })),
  ...(
    [
      [
        "05b143fd7061bdd317bd42c373c5352bec351a44fa849ded58236013126d2963",
        "Stage 1 Forge downloader",
      ],
      [
        "9ca41431df9445535b96a45529fce9f9a8b7f26c08ac8989a57787462da3342f",
        "Stage 1 Forge downloader variant",
      ],
      [
        "c5936514e05e8b1327f0df393f4d311afd080e5467062151951e94bbd7519703",
        "Stage 1 cheat-lure downloader",
      ],
      [
        "9a678140ce41bdd8c02065908ee85935e8d01e2530069df42856a1d6c902bae1",
        "Stage 1 Stargazers downloader",
      ],
      [
        "4c8a6ad89c4218507e27ad6ef4ddadb6b507020c74691d02b986a252fb5dc612",
        "Stage 2 Java stealer",
      ],
      [
        "51e423e8ab1eb49691d8500983f601989286f0552f444f342245197b74bc6fcf",
        "Stage 2 Java stealer variant",
      ],
      [
        "5d80105913e42efe58f4c325ac9b7c89857cc67e1dcab9d99f865a28ef084b37",
        "Stage 2 MixinLoader stealer",
      ],
      [
        "97df45c790994bbe7ac1a2cf83d42791c9d832fa21b99c867f5b329e0cc63f64",
        "Stage 2 Skidfuscator stealer",
      ],
      [
        "4c944b07832d5c29e7b499d9dd17a3d71f0fd918ab68694d110cbb8523b8af49",
        "Stage 2 account stealer",
      ],
      [
        "5590eaa4f11a6ed4351bc983e47d9dfd91245b89f3108bfd8b7f86e40d00b9fa",
        "Stage 2 Java stealer late variant",
      ],
      [
        "7aefd6442b09e37aa287400825f81b2ff896b9733328814fb7233978b104127f",
        "Embedded third-stage .NET stealer",
      ],
      [
        "886a694ee4be77242f501b20d37395e1a8a7a8f734f460cae269eb1309c5b196",
        "Embedded third-stage .NET stealer variant",
      ],
      [
        "a1dc479898f0798e40f63b9c1a7ee4649357abdc757c53d4a81448a5eea9169f",
        "Embedded credential stealer",
      ],
      [
        "a427eeb8eed4585f2d51b62528b8b4920e72002ab62eb6fc19289ebc2fba5660",
        "Embedded 44 CALIBER stealer",
      ],
      [
        "f08086257c74b1de394bf150ad8aacc99ca5de57b4baa0974bc1b59bb973d355",
        "Embedded third-stage payload",
      ],
    ] as const
  ).map<KnownHashRecord>(([hash, variant], index) => ({
    id: `baikal-checkpoint-${index + 1}-${hash.slice(0, 8)}`,
    family: "Baikal Stealer",
    variant,
    hashes: { sha256: hash },
    source: "Check Point Research",
    reference: BAIKAL_RESEARCH,
    firstSeen: "2025-03",
  })),
  {
    id: "skyrage-mvd-ff16aa01",
    family: "Skyrage",
    variant: "MVD JAR infector",
    hashes: {
      sha256:
        "ff16aa0148d870eead1b3099e6a6ddf865aadd0f482e2c8020ab4152d5ff6652",
      sha1: "d4d84f269d8ee523504561d9af42a11102b2bac4",
      md5: "fc6d1e1e0b8a3bf95c6e67a958f26b68",
    },
    source: MALWARE_BAZAAR,
    reference: SKYRAGE_RESEARCH,
    firstSeen: "2023-05-21",
  },
  {
    id: "skyrage-payload-28d1df25",
    family: "Skyrage",
    variant: "Botnet and credential-stealer payload",
    hashes: {
      sha256:
        "28d1df256ffb0eb1b53751e58960faaf23b77e6c91a1b898b3694fcbc4512f8d",
    },
    source: MALWARE_BAZAAR,
    reference: SKYRAGE_RESEARCH,
    firstSeen: "2023-05",
  },
  ...(
    [
      [
        "3d6969fad907c28cdbddf5a94fd77aca36d79f5e9c0b5ff744329bb774b52525",
        "7c693c5f68ff745e129201c47b672933",
        "aq.class infector component",
      ],
      [
        "57ca7a033c68d740818dbc3084418ca6bd81841cebc1dcef22cde08474de1b36",
        "730913a91b2d03262c088b98bbb703ce",
        "O.class infector component",
      ],
      [
        "0c9df0449c20dcc2b1dca02d73e2fc81234654ca7215d7dbc29d51549498bddc",
        "cc7925c04daadc1822b767110e412fe5",
        "af.class infector component",
      ],
      [
        "5f9391b0343f1e6a59594b3cbf33f4187d08bb3d921fa14e2c576fb9a7dcdad8",
        "d2ed7cb22063857dfda2d90c5335bca1",
        "ae.class infector component",
      ],
      [
        "3bdb7c146350775d55260b67895a17a01705d8fd18f8df12e492d1e187a375c7",
        "d34770ea648fb71fea1c7723baede781",
        "i.class payload component",
      ],
      [
        "6b43511e8409530e8a28a4d62e2fe9a60097946fd953f48bd6bbd9fe04cc5876",
        "3a33933a42bceecea8b5cb5da4922579",
        "r.class payload component",
      ],
      [
        "d9f7b4d99ba2d4d81d34550becfdfc6bf6e390dc31e5924acad8149f9b5b6bb5",
        "4be2b33ce8957211186600d99f718faa",
        "ar.class infector component",
      ],
      [
        "1161b4f8865993ac9353ae96c4bd0057a501763d487473735d863da870688343",
        "c4291aae2cd7714f77e05eb6647c5a4f",
        "f.class payload component",
      ],
      [
        "af76cdc822ae2051476e14d4774b2f84c6df07ded4159aa169a34724ef837448",
        "b427487d19e1a2e383f030562c1c85e7",
        "Z.class payload component",
      ],
      [
        "93150da15146aab94f812225bf02f9e611c6711c4793767461b4c4f9493d47f6",
        "74dddfef612d78ada8639a19fe2cb2ba",
        "ac.class infector component",
      ],
      [
        "b1200efa73822870f55cc5c9636c1263a90b881bef62e21bb76e96de0eee9423",
        "",
        "G.class payload component",
      ],
    ] as const
  ).map<KnownHashRecord>(([sha256Hash, md5Hash, variant], index) => ({
    id: `skyrage-component-${index + 1}-${sha256Hash.slice(0, 8)}`,
    family: "Skyrage",
    variant,
    hashes: {
      sha256: sha256Hash,
      ...(md5Hash ? { md5: md5Hash } : {}),
    },
    source: MALWARE_BAZAAR,
    reference: SKYRAGE_RESEARCH,
    firstSeen: "2023-05-21",
  })),
];

let installedHashRecords: KnownHashRecord[] = [];
let installedFingerprintRecords: KnownFingerprintRecord[] = [];

export function installThreatIntelRecords(records: KnownHashRecord[]) {
  installedHashRecords = records.slice(0, 10_000);
}

export function installThreatIntelFingerprints(
  records: KnownFingerprintRecord[],
) {
  installedFingerprintRecords = records.slice(0, 10_000);
}

export function clearInstalledThreatIntel() {
  installedHashRecords = [];
  installedFingerprintRecords = [];
}

export function getThreatIntelCounts() {
  const records = [...KNOWN_HASHES, ...installedHashRecords];
  const families = Object.entries(
    records.reduce<Record<string, number>>((result, record) => {
      result[record.family] = (result[record.family] ?? 0) + 1;
      return result;
    }, {}),
  )
    .map(([family, count]) => ({ family, count }))
    .sort((left, right) => right.count - left.count);
  return {
    builtIn: KNOWN_HASHES.length,
    installed: installedHashRecords.length,
    total: KNOWN_HASHES.length + installedHashRecords.length,
    families,
  };
}

const FAMILY_SIGNATURES: FamilySignature[] = [
  {
    id: "JARLAB-FAMILY-WEED-1",
    family: "WeedHack",
    variant: "Stage 1 v1 loader",
    reference: WEEDHACK_RESEARCH,
    minScore: 8,
    minSignals: 3,
    highConfidenceScore: 10,
    minGroupHits: { structure: 2 },
    excludes: [/dev[./]jnic[./]lXpXvp/i],
    signals: [
      {
        id: "weed-mclauncher",
        label: "me.mclauncher package",
        group: "structure",
        weight: 3,
        patterns: [/(?:^|[./])me[./]mclauncher(?:[./]|$)/i],
      },
      {
        id: "weed-staging-helper",
        label: "StagingHelper loader",
        group: "structure",
        weight: 3,
        patterns: [/\bStagingHelper\b/i],
      },
      {
        id: "weed-imcl",
        label: "IMCL in-memory class loader",
        group: "structure",
        weight: 2,
        patterns: [/(?:^|[./$])IMCL(?:[./$]|$)/],
      },
      {
        id: "weed-loaderclient",
        label: "loaderclient Fabric identity",
        group: "metadata",
        weight: 2,
        patterns: [/"?loaderclient"?/i, /\bLoaderClient\b/],
      },
      {
        id: "weed-debug-state",
        label: "WeedHack loader debug state",
        group: "campaign",
        weight: 4,
        patterns: [/\b(?:Mod init state|Resource state):\s*[MS]\d?/i],
      },
    ],
  },
  {
    id: "JARLAB-FAMILY-WEED-2",
    family: "WeedHack",
    variant: "Stage 1 v2 JNIC loader",
    reference: WEEDHACK_RESEARCH,
    minScore: 9,
    minSignals: 3,
    highConfidenceScore: 12,
    minGroupHits: { structure: 1, native: 1 },
    signals: [
      {
        id: "weed-mclauncher-v2",
        label: "me.mclauncher package",
        group: "structure",
        weight: 2,
        patterns: [/(?:^|[./])me[./]mclauncher(?:[./]|$)/i],
      },
      {
        id: "weed-jnic-lxpxvp",
        label: "Stage 1 JNIC package",
        group: "native",
        weight: 5,
        patterns: [/dev[./]jnic[./]lXpXvp/i],
      },
      {
        id: "weed-rpc-helper",
        label: "RPCHelper blockchain resolver",
        group: "campaign",
        weight: 3,
        patterns: [/\bRPCHelper\b/],
      },
      {
        id: "weed-main-entry",
        label: "MEntrypoint standalone bootstrap",
        group: "structure",
        weight: 3,
        patterns: [/\bMEntrypoint\b/],
      },
      {
        id: "weed-stage1-native-id",
        label: "Stage 1 native resource UUID",
        group: "native",
        weight: 6,
        patterns: [/c4f763d6-e34c-42e9-bba1-b80cfa5a55df/i],
      },
      {
        id: "weed-stage1-selector",
        label: "Stage 1 blockchain selector",
        group: "campaign",
        weight: 4,
        patterns: [/\b0xce6d41de\b/i, /\b0x1280a8\b/i],
      },
    ],
  },
  {
    id: "JARLAB-FAMILY-WEED-3",
    family: "WeedHack",
    variant: "Stage 2 credential stealer",
    reference: WEEDHACK_RESEARCH,
    minScore: 8,
    minSignals: 2,
    highConfidenceScore: 10,
    minGroupHits: { family: 1, native: 1 },
    signals: [
      {
        id: "weed-majanito",
        label: "Majanito package lineage",
        group: "family",
        weight: 4,
        patterns: [/(?:^|[./])dev[./]majanito(?:[./]|$)/i],
      },
      {
        id: "weed-jnic-bsomwj",
        label: "Stage 2 JNIC package",
        group: "native",
        weight: 5,
        patterns: [/dev[./]jnic[./]BSOMwJ/i],
      },
      {
        id: "weed-initialize",
        label: "initializeWeedhack entrypoint",
        group: "family",
        weight: 6,
        patterns: [/\binitializeWeedhack\b/i],
      },
      {
        id: "weed-stage2-native-id",
        label: "Stage 2 native resource UUID",
        group: "native",
        weight: 6,
        patterns: [/a125e430-2459-4702-9797-49fce5f280ae/i],
      },
    ],
  },
  {
    id: "JARLAB-FAMILY-WEED-4",
    family: "WeedHack",
    variant: "Stage 3 premium RAT",
    reference: WEEDHACK_RESEARCH,
    minScore: 7,
    minSignals: 2,
    highConfidenceScore: 10,
    minGroupHits: { family: 1, capability: 1 },
    signals: [
      {
        id: "weed-handler-package",
        label: "Majanito RAT handler package",
        group: "family",
        weight: 4,
        patterns: [/dev[./]majanito[./]handlers/i],
      },
      {
        id: "weed-jnic-fwcmer",
        label: "Stage 3 JNIC package",
        group: "family",
        weight: 5,
        patterns: [/dev[./]jnic[./]fwcMeR/i],
      },
      {
        id: "weed-keylogger-handler",
        label: "KeyLoggingHandler",
        group: "capability",
        weight: 4,
        patterns: [/\bKeyLoggingHandler\b/],
      },
      {
        id: "weed-webcam-handler",
        label: "WebcamShareHandler",
        group: "capability",
        weight: 4,
        patterns: [/\bWebcamShareHandler\b/],
      },
      {
        id: "weed-screen-handler",
        label: "ScreenShareHandler",
        group: "capability",
        weight: 4,
        patterns: [/\bScreenShareHandler\b/],
      },
    ],
  },
  {
    id: "JARLAB-FAMILY-WEED-5",
    family: "WeedHack",
    variant: "Persistence / Defender tampering",
    reference: WEEDHACK_RESEARCH,
    minScore: 8,
    minSignals: 2,
    highConfidenceScore: 11,
    signals: [
      {
        id: "weed-security-package",
        label: "Majanito security package",
        group: "structure",
        weight: 3,
        patterns: [/dev[./]majanito[./]security/i],
      },
      {
        id: "weed-updater-task",
        label: "JavaSecurityUpdater task",
        group: "persistence",
        weight: 5,
        patterns: [/\bJavaSecurityUpdater\b/i],
      },
      {
        id: "weed-security-updates",
        label: "Microsoft SecurityUpdates masquerade path",
        group: "persistence",
        weight: 5,
        patterns: [/Microsoft[\\/]+SecurityUpdates/i],
      },
      {
        id: "weed-defender-exclusion",
        label: "Microsoft Defender exclusion command",
        group: "evasion",
        weight: 5,
        patterns: [/Add-MpPreference\s+-ExclusionPath/i],
      },
      {
        id: "weed-av-helper",
        label: "AV or elevation helper",
        group: "evasion",
        weight: 3,
        patterns: [/\b(?:AVHelper|ElevationHelper)\b/],
      },
    ],
  },
  {
    id: "JARLAB-FAMILY-WEED-6",
    family: "WeedHack",
    variant: "Krypton / EtherHiding loader",
    reference: MCAFEE_WEEDHACK_RESEARCH,
    minScore: 22,
    minSignals: 4,
    highConfidenceScore: 30,
    minGroupHits: { family: 1, campaign: 2 },
    signals: [
      {
        id: "weed-krypton-initialize",
        label: "initializeWeedhack bootstrap",
        group: "family",
        weight: 8,
        patterns: [/\binitializeWeedhack\b/i],
      },
      {
        id: "weed-krypton-majanito",
        label: "Majanito Stage 2 entry point",
        group: "family",
        weight: 6,
        patterns: [/\bdev[./]majanito[./]Main\b/i],
      },
      {
        id: "weed-krypton-contract",
        label: "Krypton WeedHack delivery contract",
        group: "campaign",
        weight: 8,
        patterns: [/0x1280a841fbc1f883365d3c83122260e0b2995b74/i],
      },
      {
        id: "weed-krypton-delivery",
        label: "WeedHack delivery handler route",
        group: "campaign",
        weight: 7,
        patterns: [/\/api\/delivery\/handler\b/i],
      },
      {
        id: "weed-krypton-module",
        label: "WeedHack JAR module route",
        group: "campaign",
        weight: 7,
        patterns: [/\/files\/jar\/module\b/i],
      },
      {
        id: "weed-krypton-selector",
        label: "WeedHack contract selector",
        group: "campaign",
        weight: 6,
        patterns: [/\b0xce6d41de\b/i],
      },
      {
        id: "weed-krypton-mod-state",
        label: "Krypton Fabric bootstrap states",
        group: "execution",
        weight: 4,
        patterns: [/\bMod init state:\s*M[0-4]\b/i],
      },
      {
        id: "weed-krypton-resource-state",
        label: "Krypton resource loader states",
        group: "execution",
        weight: 4,
        patterns: [/\bResource state:\s*S[2-7]\b/i],
      },
      {
        id: "weed-krypton-javaw",
        label: "Krypton detached Java bootstrap",
        group: "execution",
        weight: 4,
        patterns: [/\bjavaw\.exe\b/i, /(?:^|\s)--jw(?:\s|$)/i],
      },
      {
        id: "weed-krypton-decoder",
        label: "Krypton call-site rotating-XOR strings",
        group: "obfuscation",
        weight: 4,
        patterns: [/\[decoded call-site rotating XOR\b/i],
      },
    ],
  },
  {
    id: "JARLAB-FAMILY-KRYPTON-1",
    family: "Krypton Stealer",
    variant: "Trojanized v15 client",
    reference: KRYPTON_HYBRID_ANALYSIS,
    minScore: 16,
    minSignals: 3,
    highConfidenceScore: 24,
    minGroupHits: { exfiltration: 1, structure: 1 },
    signals: [
      {
        id: "krypton-exfil-domain",
        label: "Krypton session-exfiltration domain",
        group: "exfiltration",
        weight: 9,
        patterns: [/\bkrypton-client\.store\b/i],
      },
      {
        id: "krypton-session-payload",
        label: "Minecraft session-token payload",
        group: "credentials",
        weight: 8,
        patterns: [
          /"username"[\s\S]{0,500}"uuid"[\s\S]{0,500}"server"[\s\S]{0,700}"token"/i,
        ],
      },
      {
        id: "krypton-tracker-class",
        label: "Krypton player tracker class",
        group: "structure",
        weight: 5,
        patterns: [
          /(?:^|[./])skid[./]gypsyy[./]DonutBBC(?:\.class)?\b/i,
          /\bUser Tracker\b/i,
        ],
      },
      {
        id: "krypton-loader-package",
        label: "Krypton obfuscated loader package",
        group: "structure",
        weight: 4,
        patterns: [/(?:^|[./])q3r[./]d4526l[./]f1a7(?:[./]|$)/i],
      },
      {
        id: "krypton-metadata-masquerade",
        label: "ImmediatelyFast metadata masquerade",
        group: "metadata",
        weight: 3,
        patterns: [/"(?:id|name)"\s*:\s*"(?:immediatelyfast|ImmediatelyFast)"/i],
      },
    ],
  },
  {
    id: "JARLAB-FAMILY-SILENT-1",
    family: "SilentNet",
    variant: "Stage 2 Fabric stealer",
    reference: SILENTNET_RESEARCH,
    minScore: 9,
    minSignals: 3,
    highConfidenceScore: 13,
    minGroupHits: { class: 2, campaign: 1 },
    signals: [
      ...[
        "lpofmCyyJlAk",
        "gGfuKbYTQc",
        "ahf_lzk",
        "CJcoV",
        "vxbTsfvniUec",
        "jvAxsmyDd",
        "kccHRVQTP",
        "TrXk",
      ].map<FamilySignal>((name, index) => ({
        id: `silent-class-${index + 1}`,
        label: `SilentNet class ${name}`,
        group: "class",
        weight: 3,
        patterns: [new RegExp(`(?:^|[./])${name}(?:[.$/]|$)`)],
      })),
      {
        id: "silent-shard-api",
        label: "SilentNet shard API",
        group: "campaign",
        weight: 5,
        patterns: [/\/shard\/(?:prefireMc|submitMinecraftLog)\b/i],
      },
      {
        id: "silent-contract",
        label: "SilentNet Polygon contract",
        group: "campaign",
        weight: 6,
        patterns: [/0x9c0a507300fd902787bb193d80fca5ce6e1bff9a/i],
      },
      {
        id: "silent-session-id",
        label: "SilentNet hardcoded session ID",
        group: "campaign",
        weight: 6,
        patterns: [/6f4dbce8-1901-5bedcd758eac/i],
      },
      {
        id: "silent-cli",
        label: "SilentNet restart arguments",
        group: "campaign",
        weight: 4,
        patterns: [/--prefireId\b/i, /--mcInfo\b/i, /(?:^|\s)-restarted\b/i],
      },
      {
        id: "silent-runtime-log",
        label: "SilentNet runtime log",
        group: "campaign",
        weight: 4,
        patterns: [/(?:_stealer|_spawn)\.log\b/i],
      },
    ],
  },
  {
    id: "JARLAB-FAMILY-SILENT-2",
    family: "SilentNet",
    variant: "C2 / configuration",
    reference: SILENTNET_RESEARCH,
    minScore: 10,
    minSignals: 2,
    highConfidenceScore: 13,
    minGroupHits: { campaign: 2 },
    signals: [
      {
        id: "silent-shard-api-config",
        label: "SilentNet shard API",
        group: "campaign",
        weight: 6,
        patterns: [/\/shard\/prefireMc\b/i, /\/shard\/submitMinecraftLog\b/i],
      },
      {
        id: "silent-cdn-api",
        label: "SilentNet concealed CDN route",
        group: "campaign",
        weight: 5,
        patterns: [
          /\/cdn\/e\/3b8f6d2a9c1e\b/i,
          /\/cdn\/f3a7d1e9b5c2\b/i,
          /\/cdn\/b7e2f1d9c3a6\b/i,
        ],
      },
      {
        id: "silent-contract-config",
        label: "SilentNet Polygon contract",
        group: "campaign",
        weight: 7,
        patterns: [/0x9c0a507300fd902787bb193d80fca5ce6e1bff9a/i],
      },
      {
        id: "silent-aes-key",
        label: "SilentNet v2 AES key",
        group: "campaign",
        weight: 7,
        patterns: [
          /dK9mT3nR7xQ2pL8wF4jH6yB1cN5gA0sZ12345/,
          /\bdK9mT3(?:nR7xQ2pL8w)?\b/,
        ],
      },
      {
        id: "silent-session-config",
        label: "SilentNet hardcoded session ID",
        group: "campaign",
        weight: 7,
        patterns: [/6f4dbce8-1901-5bedcd758eac/i],
      },
      {
        id: "silent-header",
        label: "SilentNet runtime verification header",
        group: "campaign",
        weight: 5,
        patterns: [
          /\bX-Runtime-Env\b/i,
          /\bx-cdn-origin-verify\b/i,
          /\bX-Edge-Cache-Revalidate\b/i,
        ],
      },
      {
        id: "silent-cli-config",
        label: "SilentNet restart arguments",
        group: "campaign",
        weight: 4,
        patterns: [/--prefireId\b/i, /--mcInfo\b/i, /(?:^|\s)-restarted\b/i],
      },
      {
        id: "silent-log-config",
        label: "SilentNet runtime log",
        group: "campaign",
        weight: 4,
        patterns: [/(?:_stealer|_spawn)\.log\b/i],
      },
      {
        id: "silent-domain",
        label: "SilentNet campaign domain",
        group: "campaign",
        weight: 6,
        patterns: [
          /\b(?:www\.)?silentnet\.st\b/i,
          /\b(?:[a-z0-9-]+\.)*thisisafalsepositive\.(?:ru|st)\b/i,
          /\bsltnnt\.ru\b/i,
        ],
      },
      {
        id: "silent-polygon-rpc",
        label: "Polygon RPC dead-drop infrastructure",
        group: "infrastructure",
        weight: 1,
        patterns: [
          /\bpolygon-rpc\.com\b/i,
          /\bpolygon\.drpc\.org\b/i,
          /\bpolygon-bor-rpc\.publicnode\.com\b/i,
          /\brpc-mainnet\.matic\.network\b/i,
          /\bpolygon\.llnodes\.net\b/i,
        ],
      },
      {
        id: "silent-contract-selector",
        label: "SilentNet contract call selector",
        group: "campaign",
        weight: 6,
        patterns: [/\b0xce6d41de\b/i],
      },
      {
        id: "silent-runtime-stage",
        label: "SilentNet portable Python stage",
        group: "payload",
        weight: 4,
        patterns: [/\bjre-embedded\b/i, /\bmain\.py\b/i],
      },
    ],
  },
  {
    id: "JARLAB-FAMILY-SILENT-4",
    family: "SilentNet",
    variant: "Packed Stage 2 downloader",
    reference: SILENTNET_RESEARCH,
    minScore: 18,
    minSignals: 4,
    highConfidenceScore: 25,
    minGroupHits: { obfuscation: 1, campaign: 2, payload: 1 },
    signals: [
      {
        id: "silent-xor-packer",
        label: "runtime string packer",
        group: "obfuscation",
        weight: 6,
        patterns: [
          /JARLAB_(?:XOR|CHARACTER)_PACKED_STRINGS=(?:[8-9]|[1-9]\d{1,3})\b/,
        ],
      },
      {
        id: "silent-xor-shard",
        label: "SilentNet shard API",
        group: "campaign",
        weight: 7,
        patterns: [/\/shard\/(?:prefireMc|submitMinecraftLog)\b/i],
      },
      {
        id: "silent-xor-cdn",
        label: "SilentNet encrypted CDN route",
        group: "campaign",
        weight: 6,
        patterns: [/\/cdn\/(?:e|f3a7d1e9b5c2|b7e2f1d9c3a6)\//i],
      },
      {
        id: "silent-xor-selector",
        label: "SilentNet contract selector",
        group: "campaign",
        weight: 6,
        patterns: [/\b0xce6d41de\b/i],
      },
      {
        id: "silent-xor-header",
        label: "SilentNet runtime header",
        group: "campaign",
        weight: 5,
        patterns: [/\bX-(?:Runtime-Env|Edge-Cache-Revalidate)\b/i],
      },
      {
        id: "silent-xor-log",
        label: "SilentNet stealer log",
        group: "campaign",
        weight: 5,
        patterns: [/\b_stealer\.log\b/i],
      },
      {
        id: "silent-xor-stage",
        label: "portable Python payload",
        group: "payload",
        weight: 5,
        patterns: [/\bjre-embedded\b/i, /\bmain\.py\b/i],
      },
      {
        id: "silent-xor-cli",
        label: "SilentNet restart arguments",
        group: "payload",
        weight: 4,
        patterns: [/--prefireId\b/i, /--mcInfo\b/i, /(?:^|\s)-restarted\b/i],
      },
    ],
  },
  {
    id: "JARLAB-FAMILY-SILENT-3",
    family: "SilentNet",
    variant: "Stage 3 stager artifacts",
    reference: SILENTNET_RESEARCH,
    minScore: 8,
    minSignals: 3,
    highConfidenceScore: 11,
    signals: [
      ...[
        "PerfMonHost",
        "IManagementEngine",
        "INetworkService",
        "WindowsUpdateTask",
        "app.pyd",
      ].map<FamilySignal>((name, index) => ({
        id: `silent-stage3-${index + 1}`,
        label: `Stage 3 artifact ${name}`,
        group: "artifact",
        weight: 3,
        patterns: [new RegExp(name.replace(".", "\\."), "i")],
      })),
    ],
  },
  {
    id: "JARLAB-FAMILY-BAIKAL-1",
    family: "Baikal Stealer",
    variant: "Stargazers Ghost Java loader and stealer",
    reference: BAIKAL_RESEARCH,
    minScore: 15,
    minSignals: 4,
    highConfidenceScore: 22,
    minGroupHits: { structure: 1, delivery: 1, collection: 1 },
    signals: [
      {
        id: "baikal-package",
        label: "me.baikal.club package",
        group: "structure",
        weight: 7,
        patterns: [/(?:^|[./])me[./]baikal[./]club(?:[./]|$)/i],
      },
      {
        id: "baikal-fml-loader",
        label: "Forge coremod loading interface",
        group: "structure",
        weight: 2,
        patterns: [/\bIFMLLoadingPlugin\b/i],
      },
      {
        id: "baikal-paste",
        label: "Baikal stage URL paste",
        group: "delivery",
        weight: 6,
        patterns: [/\bpastebin\.com\/raw\/xCa3vSiP\b/i, /\bxCa3vSiP\b/],
      },
      {
        id: "baikal-delivery-ip",
        label: "Baikal second-stage delivery server",
        group: "delivery",
        weight: 7,
        patterns: [/\b147\.45\.79\.104(?:\/download)?\b/i],
      },
      {
        id: "baikal-mixin-loader",
        label: "MixinLoader second-stage name",
        group: "delivery",
        weight: 5,
        patterns: [/\bMixinLoader-v2\.4\.jar\b/i],
      },
      {
        id: "baikal-exfil-paste",
        label: "Baikal exfiltration configuration paste",
        group: "delivery",
        weight: 5,
        patterns: [/\bpastebin\.com\/raw\/C9QvUqi3\b/i, /\bC9QvUqi3\b/],
      },
      {
        id: "baikal-account-schema",
        label: "Minecraft and launcher token JSON schema",
        group: "collection",
        weight: 6,
        patterns: [
          /"username"\s*:[\s\S]{0,300}"uuid"\s*:[\s\S]{0,300}"token"\s*:[\s\S]{0,500}"(?:feather|essentials|lunar|discord)"/i,
        ],
      },
      {
        id: "baikal-launcher-files",
        label: "Multiple Minecraft launcher account stores",
        group: "collection",
        weight: 4,
        patterns: [
          /\bFeather[\\/].*accounts\.json\b/i,
          /\bLunar[\\/].*accounts\.json\b/i,
          /\bmicrosoft_accounts\.jcon\b/i,
        ],
      },
      {
        id: "baikal-sshaccess",
        label: "SSHaccess staged payload runner",
        group: "execution",
        weight: 4,
        patterns: [/\bSSHaccess\b/i, /\bdownloadFile\b[\s\S]{0,180}\brunFile\b/i],
      },
      {
        id: "baikal-external-ip",
        label: "External IP discovery",
        group: "discovery",
        weight: 2,
        patterns: [/\bcheckip\.amazonaws\.com\b/i],
      },
      {
        id: "baikal-anti-vm",
        label: "VM and analyst process blocklist",
        group: "evasion",
        weight: 3,
        patterns: [
          /\bVBoxTray\.exe\b/i,
          /\bHTTPDebuggerUI\.exe\b/i,
          /\bWireshark\.exe\b/i,
        ],
      },
    ],
  },
  {
    id: "JARLAB-FAMILY-SKYRAGE-1",
    family: "Skyrage",
    variant: "MVD JAR infector and credential-stealing botnet",
    reference: SKYRAGE_RESEARCH,
    minScore: 14,
    minSignals: 3,
    highConfidenceScore: 21,
    minGroupHits: { infrastructure: 1, infection: 1 },
    signals: [
      {
        id: "skyrage-connect",
        label: "Skyrage botnet endpoint",
        group: "infrastructure",
        weight: 8,
        patterns: [/\bconnect\.skyrage\.de\b/i],
      },
      {
        id: "skyrage-files",
        label: "Skyrage payload host",
        group: "infrastructure",
        weight: 7,
        patterns: [/\bfiles\.skyrage\.de\b/i],
      },
      {
        id: "skyrage-port",
        label: "Skyrage command port",
        group: "infrastructure",
        weight: 4,
        patterns: [/\b17929\b/],
      },
      {
        id: "skyrage-config",
        label: "Skyrage infected-JAR marker",
        group: "infection",
        weight: 7,
        patterns: [/\bplugin-config\.bin\b/i],
      },
      {
        id: "skyrage-service",
        label: "Skyrage vmd-gnu service",
        group: "persistence",
        weight: 7,
        patterns: [/\bvmd-gnu\b/i],
      },
      {
        id: "skyrage-payload-route",
        label: "Skyrage commanded payload route",
        group: "delivery",
        weight: 5,
        patterns: [/skyrage\.de\/q{6,}/i],
      },
      {
        id: "skyrage-signature-strip",
        label: "JAR signature stripping list",
        group: "infection",
        weight: 5,
        patterns: [
          /META-INF\/CERTIFIC\.RSA/i,
          /META-INF\/CERTIFIC\.SF/i,
          /META-INF\/CERT\.SF/i,
        ],
      },
      {
        id: "skyrage-java-replacement",
        label: "Java runtime executable replacement",
        group: "infection",
        weight: 4,
        patterns: [
          /java\.home[\s\S]{0,180}(?:javaw?\.exe|\/bin\/java)\b/i,
        ],
      },
    ],
  },
  {
    id: "JARLAB-FAMILY-FRACTUREISER-1",
    family: "Fractureiser",
    variant: "Multi-stage mod infection chain",
    reference: FRACTUREISER_RESEARCH,
    minScore: 10,
    minSignals: 3,
    highConfidenceScore: 14,
    minGroupHits: { structure: 1, delivery: 1 },
    signals: [
      {
        id: "fractureiser-neko-package",
        label: "Fractureiser Neko package",
        group: "structure",
        weight: 5,
        patterns: [/(?:^|[./])dev[./]neko[./]nekoclient(?:[./]|$)/i],
      },
      {
        id: "fractureiser-stage-file",
        label: "libWebGL64 Stage 2 payload",
        group: "delivery",
        weight: 5,
        patterns: [/\blibWebGL64\.jar\b/i],
      },
      {
        id: "fractureiser-pages-host",
        label: "Fractureiser delivery host",
        group: "delivery",
        weight: 5,
        patterns: [/\bfiles-8ie\.pages\.dev\b/i],
      },
      {
        id: "fractureiser-neko-run",
        label: "Neko persistence marker",
        group: "persistence",
        weight: 4,
        patterns: [/\bneko\.run\b/i, /(?:^|[\\/])\.ref(?:[\\/]|$)/i],
      },
      {
        id: "fractureiser-task",
        label: "Edge update scheduled task",
        group: "persistence",
        weight: 4,
        patterns: [/\bMicrosoftEdgeUpdateTaskMachineVM\b/i],
      },
      {
        id: "fractureiser-ip",
        label: "Fractureiser command server",
        group: "delivery",
        weight: 5,
        patterns: [/\b85\.217\.144\.130\b/],
      },
      {
        id: "fractureiser-url-loader",
        label: "Remote URL class loading",
        group: "structure",
        weight: 2,
        patterns: [/\bURLClassLoader\b/i],
      },
    ],
  },
];

const algorithmPriority: HashAlgorithm[] = ["sha256", "sha1", "md5"];

function clippedEvidence(value: string) {
  const clean = value.trim();
  return clean.length > 190 ? `${clean.slice(0, 187)}…` : clean;
}

function patternMatches(pattern: RegExp, value: string) {
  pattern.lastIndex = 0;
  return pattern.test(value);
}

export function matchKnownHashes(
  hashes: Partial<Record<HashAlgorithm, string>>,
  target: KnownHashMatch["target"],
  entry?: string,
) {
  const matches: KnownHashMatch[] = [];
  for (const record of [...KNOWN_HASHES, ...installedHashRecords]) {
    const algorithm = algorithmPriority.find(
      (candidate) =>
        hashes[candidate] &&
        record.hashes[candidate] === hashes[candidate]?.toLowerCase(),
    );
    if (!algorithm) continue;
    matches.push({
      id: `${record.id}:${target}:${entry ?? ""}`,
      family: record.family,
      variant: record.variant,
      algorithm,
      hash: hashes[algorithm]!,
      target,
      entry,
      source: record.source,
      reference: record.reference,
      firstSeen: record.firstSeen,
      trust: record.trust ?? "built-in",
      packId: record.packId,
    });
  }
  return matches;
}

// Structural near-duplicate matching. Exact hashes cannot see a sample that was
// rebuilt, repacked or lightly patched, which is the normal way these campaigns
// spread; the archive simhash is built from per-method API-category and shape
// features, so it survives all three.
//
// The similarity metric and the fingerprint itself are both reused unchanged
// from fingerprints.ts. What is new here is only the comparison against a
// corpus and the per-record threshold, which is the pack author stating how
// much resemblance they are willing to vouch for.
export function matchFingerprints(simhash64: string): FingerprintMatch[] {
  const candidates = installedFingerprintRecords
    .map((record) => ({
      record,
      similarity: simhashSimilarity(simhash64, record.simhash64),
    }))
    // Below the record's own threshold this is simply not a match.
    .filter((candidate) => candidate.similarity >= candidate.record.threshold);

  if (!candidates.length) return [];

  // Competing candidates resolve to the best similarity, not the first one
  // that cleared its bar: a pack holding several samples of a family would
  // otherwise report whichever happened to be listed first. Exact ties are all
  // returned, because two families matching equally well is a real ambiguity
  // and hiding it behind an arbitrary pick would be dishonest.
  const best = Math.max(
    ...candidates.map((candidate) => candidate.similarity),
  );
  return candidates
    .filter((candidate) => candidate.similarity === best)
    .map(({ record, similarity }) => ({
      id: `${record.family}:${record.variant}:${record.simhash64}`,
      family: record.family,
      variant: record.variant,
      similarity,
      threshold: record.threshold,
      simhash64: record.simhash64,
      source: record.source,
      reference: record.reference,
      firstSeen: record.firstSeen,
      trust: record.trust ?? ("built-in" as const),
      packId: record.packId,
    }));
}

export function detectFamilySignatures(contexts: ThreatContext[]) {
  const joined = contexts.flatMap((context) => context.values);
  const matches: FamilySignatureMatch[] = [];

  for (const signature of FAMILY_SIGNATURES) {
    if (
      signature.excludes?.some((pattern) =>
        joined.some((value) => patternMatches(pattern, value)),
      )
    ) {
      continue;
    }

    const matchedSignals: FamilySignatureMatch["matchedSignals"] = [];
    for (const signal of signature.signals) {
      let evidence:
        | { source: string; value: string }
        | undefined;
      for (const context of contexts) {
        const value = context.values.find((candidate) =>
          signal.patterns.some((pattern) =>
            patternMatches(pattern, candidate),
          ),
        );
        if (value !== undefined) {
          evidence = { source: context.source, value };
          break;
        }
      }
      if (!evidence) continue;
      matchedSignals.push({
        signalId: signal.id,
        label: signal.label,
        group: signal.group,
        source: evidence.source,
        evidence: clippedEvidence(evidence.value),
        weight: signal.weight,
      });
    }

    const score = matchedSignals.reduce(
      (total, signal) => total + signal.weight,
      0,
    );
    const groupHits = matchedSignals.reduce<Record<string, number>>(
      (result, signal) => {
        result[signal.group] = (result[signal.group] ?? 0) + 1;
        return result;
      },
      {},
    );
    const groupsSatisfied = Object.entries(
      signature.minGroupHits ?? {},
    ).every(([group, minimum]) => (groupHits[group] ?? 0) >= minimum);

    if (
      score < signature.minScore ||
      matchedSignals.length < signature.minSignals ||
      !groupsSatisfied
    ) {
      continue;
    }

    matches.push({
      signatureId: signature.id,
      family: signature.family,
      variant: signature.variant,
      confidence:
        score >= signature.highConfidenceScore ? "high" : "medium",
      score,
      maxScore: signature.signals.reduce(
        (total, signal) => total + signal.weight,
        0,
      ),
      matchedSignals: matchedSignals.sort(
        (left, right) => right.weight - left.weight,
      ),
      reference: signature.reference,
    });
  }

  return matches;
}

export const THREAT_INTEL_VERSION = "2026.08.24.1";
export const KNOWN_HASH_COUNT = KNOWN_HASHES.length;
